﻿import 'bootstrap/dist/css/bootstrap.min.css'
import { useKanban } from '../../hooks/useKanban'
import useTicketCatalogs from '../../hooks/useTicketCatalogs'
import useTicketFilters from '../../hooks/useTicketFilters'
import KanbanColumn from './KanbanColumn'
import TicketSearchBar from '../ticket/TicketSearchBar'
import { useNavigate } from 'react-router-dom'

/**
 * Criterios avanzados del tablero. No incluye el estado: las columnas ya son
 * los estados.
 */
export const KANBAN_FILTER_FIELDS = [
  'queueId',
  'origin',
  'type',
  'priorityId',
  'responsibleId',
  'authorId',
  'sla',
  'createdFrom',
  'createdTo',
]

/**
 * Componente Kanban para la gestión visual de tickets.
 * * * **Funcionalidades**:
 * - Búsqueda avanzada (texto, plataforma, tipo, prioridad, responsable, autor,
 *   SLA y fechas).
 * - Autor (con foto) en cada tarjeta.
 * - Tablero con scroll horizontal adaptativo.
 * - Soporte opcional para Drag & Drop (habilitable mediante props).
 * - Navegación automática al detalle del ticket al seleccionar una tarjeta.
 *
 * @component
 * @param {Object} props - Propiedades del componente.
 * @param {Array<Object>} [props.tickets=[]] - Lista de tickets a mostrar en el tablero.
 * @param {boolean} [props.disableDrag=false] - Indica si se debe deshabilitar la funcionalidad de arrastrar y soltar.
 * @param {boolean} [props.showSearch=true] - Muestra el buscador propio. Pásalo en
 *   `false` cuando la página ya filtra los tickets desde su barra de herramientas.
 * * @returns {JSX.Element} Un tablero Kanban funcional con barra de búsqueda.
 */
const Kanban = ({ tickets = [], disableDrag = false, showSearch = true }) => {
  const navigate = useNavigate()
  const catalogs = useTicketCatalogs()
  const search = useTicketFilters(tickets, catalogs)
  const filteredTickets = showSearch ? search.filtered : tickets

  /**
   * Hook personalizado que gestiona la lógica de estado del tablero,
   * las columnas y los eventos de Drag & Drop.
   */
  const {
    board,
    columns,
    loading: isLoading,
    handleDragStart,
    handleDrop,
  } = useKanban(filteredTickets)

  /**
   * Gestiona la navegación hacia la vista detallada del ticket seleccionado.
   * @param {Object} e - Objeto de datos de la tarjeta seleccionada.
   * @param {string|number} e.id - Identificador único del ticket.
   */
  const onSelect = (e) => {
    navigate(`/ticket/${e.id}`)
  }

  return (
    <div className="kanban-wrapper w-100 d-flex flex-column">
      {/* SEARCH BAR */}
      {showSearch && (
        <div className="mb-3">
          <TicketSearchBar
            filters={search.filters}
            onChange={search.setFilter}
            onClear={search.clearFilters}
            options={search.options}
            activeCount={search.activeCount}
            fields={KANBAN_FILTER_FIELDS}
          />
        </div>
      )}

      {/* BOARD AREA CON OVERFLOW */}
      <div className="kanban-board-container">
        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center h-100">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
          </div>
        ) : (
          <div className="kanban-horizontal-scroll">
            {Object.entries(board).map(([columnKey, cards]) => {
              const column = columns.find(
                (c) => Number(c.id) === Number(columnKey)
              )

              return (
                <div key={columnKey} className="kanban-column-wrapper">
                  <KanbanColumn
                    columnKey={columnKey}
                    title={column?.name}
                    color={column?.color}
                    cards={cards}
                    onDrop={disableDrag ? undefined : handleDrop}
                    onDragStart={disableDrag ? undefined : handleDragStart}
                    onSelect={onSelect}
                    disableDrag={disableDrag}
                    authors={catalogs.employeeByAuthId}
                  />
                </div>
              )
            })}
          </div>
        )}
      </div>

      <style>{`
        /* El tablero ocupa el alto que le deja su contenedor (páginas
           .page-fill): sólo scrollean las tarjetas dentro de cada columna. */
        .kanban-wrapper { flex: 1 1 auto; min-height: 0; }
        .kanban-board-container {
          flex: 1 1 auto;
          min-height: 320px;
          overflow: hidden;
          display: flex;
        }

        /* Scroll horizontal para el tablero */
        .kanban-horizontal-scroll {
          display: flex;
          flex-direction: row;
          gap: 0;
          overflow-x: auto;
          overflow-y: hidden;
          padding-bottom: 8px;
          width: 100%;
          align-items: stretch;
        }

        /* Las columnas se reparten todo el ancho; si no caben en el mínimo,
           aparece el scroll horizontal. */
        .kanban-column-wrapper + .kanban-column-wrapper {
          border-left: 1px solid var(--gm-border);
        }
        .kanban-column-wrapper {
          flex: 1 1 0;
          min-width: 250px;
          height: 100%; /* Ocupa todo el alto del scroll horizontal */
          display: flex;
          flex-direction: column;
        }

        /* Personalización de la barra de scroll (opcional) */
        .kanban-horizontal-scroll::-webkit-scrollbar {
          height: 8px;
        }
        .kanban-horizontal-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .kanban-horizontal-scroll::-webkit-scrollbar-thumb {
          background: var(--gm-border);
          border-radius: 10px;
        }
        .kanban-horizontal-scroll::-webkit-scrollbar-thumb:hover {
          background: var(--gm-text-faint);
        }
      `}</style>
    </div>
  )
}

export default Kanban
